import { Image, Label, UIHeading3, UIText2 } from '@cian/ui-kit';

import { type FC, useCallback } from 'react';

import { type RoomsInfoItem, RoomsList } from 'shared/components/RoomsList';
import { Transport } from 'shared/components/Transport';
import { type RemotenessTypes } from 'shared/types/RemotenessTypes';
import { type TransportTypes } from 'shared/types/TransportTypes';
import { extractUndefined } from 'shared/utils/extractUndefined';

import styles from './ResidentialComplexesCardDefault.css';

type TransportType = {
  id: number;
  name: string;
  iconColor?: string | null;
  remoteness?: string | null;
  remotenessType?: RemotenessTypes | null;
  iconType?: TransportTypes | null;
};

type ResidentialComplexesCardDefaultProps = {
  title: string;
  subtitle: string | null;
  transports: TransportType[];
  imageUrl: string;
  roomsInfo: RoomsInfoItem[];
  promoLabel: string | null;
  url: string;
  newbuildingId: number;
  onJkCardClick: (newbuildingId: number) => void;
};

export const ResidentialComplexesCardDefault: FC<ResidentialComplexesCardDefaultProps> = ({
  title,
  subtitle,
  transports,
  imageUrl,
  roomsInfo,
  promoLabel,
  url,
  newbuildingId,
  onJkCardClick,
}) => {
  const handleClick = useCallback((): void => {
    onJkCardClick(newbuildingId);
  }, [newbuildingId, onJkCardClick]);

  return (
    <a
      className={styles['container']}
      data-testid="residential-complexes-card-default"
      href={url}
      rel="noopener noreferrer"
      target="_blank"
      onClick={handleClick}
    >
      <div className={styles['image-wrapper']}>
        {!!promoLabel && (
          <div className={styles['promo-label-wrapper']}>
            <Label background="control-inverted-primary-default" color="#fc4c02" data-testid="promo-label">
              {promoLabel}
            </Label>
          </div>
        )}
        <div className={styles['image-wrapper-inner']}>
          <Image alt={title} data-testid="image" height={200} objectFit="cover" src={imageUrl} width="100%" />
        </div>
      </div>
      <div className={styles['content']}>
        <div className={styles['title-wrapper']}>
          <UIHeading3 color="current_color" data-testid="title">
            {title}
          </UIHeading3>
        </div>
        {!!subtitle && (
          <UIText2 color="text-secondary-default" data-testid="subtitle">
            {subtitle}
          </UIText2>
        )}

        {!!transports.length && (
          <div className={styles['transports-wrapper']} data-testid="transports-wrapper">
            {transports.map(transport => (
              <Transport
                iconColor={extractUndefined(transport.iconColor)}
                iconType={extractUndefined(transport.iconType)}
                key={transport.id}
                name={transport.name}
                remoteness={extractUndefined(transport.remoteness)}
                remotenessIconColor="icon-secondary-default"
                remotenessType={extractUndefined(transport.remotenessType)}
                textColor="text-secondary-default"
              />
            ))}
          </div>
        )}

        {!!roomsInfo.length && (
          <div className={styles['rooms-info-wrapper']} data-testid="rooms-list-wrapper">
            <RoomsList list={roomsInfo} maxItems={4} />
          </div>
        )}
      </div>
    </a>
  );
};
